// Components
// Libs
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';

// API
import getInvoice from '@/common/api/invoices/getInvoice';
import getInvoices from '@/common/api/invoices/getInvoices';
import Snack from '@/common/components/molecules/Snack/Snack';
// Constants
import { ENDPOINT_INVOICES } from '@/common/constants';

const useInvoice = ({ disabled, isLoading, isSuccess }) => {
  const { t } = useTranslation();

  /**
   * Mutation to get invoice
   */
  const {
    mutate: mutateGetInvoice,
    isLoading: isLoadingGetInvoice,
    isSuccess: isSuccessGetInvoice,
  } = useMutation(getInvoice);

  /**
   * Callback given to the children component to get invoice
   * @param {object} order
   * @param {object} invoice
   */
  const onDownloadInvoice = useCallback(
    ({ order, invoice }) => {
      mutateGetInvoice(
        { queryKey: [ENDPOINT_INVOICES, order.number, invoice.id] },
        {
          onSuccess: () => {
            //
          },
          onError: (error) => {
            if (error.response.status === 500) {
              toast.error(
                <Snack
                  message={t('common:messages:forms.errors.server_error')}
                />,
              );
            } else {
              toast.error(
                <Snack message={t('common:messages:forms.errors.default')} />,
              );
            }
          },
        },
      );
    },
    [mutateGetInvoice, t],
  );

  /**
   * Mutation to get invoices
   */
  const {
    mutate: mutateGetInvoices,
    isLoading: isLoadingGetInvoices,
    isSuccess: isSuccessGetInvoices,
  } = useMutation(getInvoices);

  /**
   * Callback given to the children component to get invoices
   * @param {number} orderNumber
   * @param {function} onSuccess
   */
  const onGetInvoices = useCallback(
    ({ orderNumber, onSuccess = () => {} }) => {
      mutateGetInvoices(
        { queryKey: [ENDPOINT_INVOICES, orderNumber] },
        {
          onSuccess,
          onError: (error) => {
            if (error.response.status === 500) {
              toast.error(
                <Snack
                  message={t('common:messages:forms.errors.server_error')}
                />,
              );
            } else {
              toast.error(
                <Snack message={t('common:messages:forms.errors.default')} />,
              );
            }
          },
        },
      );
    },
    [mutateGetInvoices, t],
  );

  /**
   * Check if action is disabled
   * @type {boolean}
   */
  const isDisabled = useMemo(
    () => ({
      loading: isLoading || isLoadingGetInvoice || isLoadingGetInvoices,
      disabled:
        isLoading || isLoadingGetInvoice || isLoadingGetInvoices || disabled,
    }),
    [disabled, isLoading, isLoadingGetInvoice, isLoadingGetInvoices],
  );

  return {
    isDisabled,
    isSuccess: isSuccess || isSuccessGetInvoice || isSuccessGetInvoices,
    onDownloadInvoice,
    onGetInvoices,
  };
};

export default useInvoice;
